// Referral card shown in the Me / Settings tab.
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { getMyReferralCode, getMyReferrals } from "@/lib/referrals.functions";
import { shareUrlForCode, shareTextForCode } from "@/lib/referrals-shared";

const GD = "#4B1F4F";
const GR = "#6E4B71";
const T = "#FF5C7A";
const TL = "#FFE4EA";
const TM = "#FFC1CD";
const WH = "#ffffff";

interface Ref {
  id: string;
  status: string;
  createdAt: string;
  creditedAt: string | null;
  maskedEmail: string;
}

export function ReferralCard() {
  const fetchCode = useServerFn(getMyReferralCode);
  const fetchRefs = useServerFn(getMyReferrals);
  const [code, setCode] = useState<string | null>(null);
  const [refs, setRefs] = useState<Ref[]>([]);
  const [creditedCount, setCreditedCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [a, b] = await Promise.all([fetchCode(), fetchRefs()]);
        if (cancelled) return;
        setCode(a.code);
        setRefs(b.referrals);
        setCreditedCount(b.creditedCount);
      } catch (e) {
        console.error("[ReferralCard] load failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchCode, fetchRefs]);

  const url = code ? shareUrlForCode(code) : "";
  const shareText = code ? shareTextForCode(code) : "";

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied!");
    } catch {
      toast.error("Couldn't copy — try selecting the link manually.");
    }
  };

  const handleShare = async () => {
    if (!shareText) return;
    const nav = typeof navigator !== "undefined" ? (navigator as Navigator & { share?: (d: ShareData) => Promise<void> }) : null;
    if (nav?.share) {
      try {
        await nav.share({ title: "KnowMeno", text: shareText, url });
        return;
      } catch {
        // user cancelled — fall through to copy
      }
    }
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success("Share message copied!");
    } catch {
      toast.error("Couldn't copy share message.");
    }
  };

  if (loading) {
    return (
      <div style={{ background: WH, borderRadius: 18, padding: 16, marginBottom: 14, color: GR, fontSize: 13 }}>
        Loading your referral link…
      </div>
    );
  }

  if (!code) return null;

  return (
    <div style={{ background: TL, borderRadius: 18, padding: 16, marginBottom: 14, border: `1.5px solid ${TM}` }}>
      <div style={{ fontSize: 16, fontWeight: 600, color: GD, marginBottom: 6 }}>
        Give a month, get a month 🎁
      </div>
      <div style={{ fontSize: 13, color: GD, lineHeight: 1.55, marginBottom: 12 }}>
        Share KnowMeno with a friend. When she signs up and tracks regularly for her first 30 days, you earn a free month.
      </div>

      <div
        style={{
          background: WH,
          borderRadius: 12,
          padding: "10px 12px",
          marginBottom: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          wordBreak: "break-all",
        }}
      >
        <span style={{ fontSize: 13, color: GD, fontFamily: "ui-monospace, monospace" }}>{url}</span>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <button
          onClick={handleCopy}
          style={{
            flex: 1,
            padding: "10px",
            borderRadius: 12,
            background: WH,
            color: GD,
            border: `1.5px solid ${TM}`,
            fontSize: 13,
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          Copy link
        </button>
        <button
          onClick={handleShare}
          style={{
            flex: 1,
            padding: "10px",
            borderRadius: 12,
            background: T,
            color: WH,
            border: "none",
            fontSize: 13,
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          Share
        </button>
      </div>

      {refs.length === 0 ? (
        <div style={{ fontSize: 12.5, color: GR, lineHeight: 1.5, padding: "10px 12px", background: WH, borderRadius: 10 }}>
          No referrals yet — share your link to get started 💜
        </div>
      ) : (
        <div style={{ background: WH, borderRadius: 12, padding: "8px 12px", marginBottom: 10 }}>
          {refs.map((r, i) => {
            const unlockDate = (() => {
              if (r.status !== "pending") return null;
              const d = new Date(new Date(r.createdAt).getTime() + 30 * 86400_000);
              return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
            })();
            return (
              <div
                key={r.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: i < refs.length - 1 ? "0.5px solid #E8D5DA" : "none",
                  fontSize: 13,
                  gap: 8,
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <span style={{ color: GD }}>{r.maskedEmail}</span>
                  {unlockDate && (
                    <span style={{ fontSize: 11, color: GR, marginTop: 2 }}>
                      Free month unlocks ~{unlockDate}
                    </span>
                  )}
                </div>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 500,
                    padding: "3px 9px",
                    borderRadius: 20,
                    background: r.status === "credited" ? "#E8F5E8" : r.status === "rejected" ? "#F5E8E8" : "#FFF4D6",
                    color: r.status === "credited" ? "#2E7D32" : r.status === "rejected" ? "#9B3D3D" : "#8A6D1F",
                    flexShrink: 0,
                  }}
                >
                  {r.status === "credited" ? "Credited" : r.status === "rejected" ? "Not eligible" : "Pending"}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ fontSize: 13, color: GD, textAlign: "center", marginTop: 4 }}>
        You've banked <strong>{creditedCount}</strong> free month{creditedCount === 1 ? "" : "s"} 🎉
      </div>
    </div>
  );
}
